import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { channelWa, isApiError } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Badge, CopyButton, SimpleGrid } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, DataTable, StatusBadge, EmptyState, Stepper } from '@repletoryreach/sdk-ui/data-display';
import { Drawer, Menu, Modal, Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { TextInput, PhoneInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT, type Translate } from '../../i18n/I18nProvider';
import { phoneLabels, phoneRefusalText } from '../../lib/phone';
import { formatDate, formatDateTime, type Indexed, formatCount } from '../../lib/format';
import { usePersistedState } from '../../lib/usePersistedState';
import { ConnectWhatsApp, PaymentMethodChecklist, OWN_ACCOUNT_UPGRADE_PATH } from './ConnectWhatsApp';
import { META_NUMBERS_URL, META_PAYMENTS_URL } from '../../lib/meta-billing';
import { MoveFromApp } from './MoveFromApp';
import { MetaRefusalNotice, refusalFrom, type Refusal } from './MetaRefusalNotice';
import { CallingDrawer } from './CallingDrawer';
import { useVoiceStatus } from '../../lib/voice';
import { HelpButton } from '../../app/guides/HelpButton';
import './numbers.css';

/*
 * META_NUMBERS_URL is where a business finds its phone-number-id (pointed at,
 * never just named) and, on its own WhatsApp account, changes a display name.
 * META_PAYMENTS_URL is where a payment method goes on a WhatsApp Business
 * Account. Meta 131042 ("Business eligibility payment issue") means there is
 * none; on the business's own account nobody here can fix it, so the page
 * does not merely name the place: it opens it.
 */

/** Meta phone-number-ids are 15 or 16 digits. */
const META_ID_SHAPE = /^\d{15,16}$/;

export default function NumbersPage() {
  const t = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [attaching, setAttaching] = useState(false);
  const [connectNotice, setConnectNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<channelWa.PhoneNumber | null>(null);
  // WhatsApp calls (3 October 2026): offered only where Voice exists for this account.
  const [calling, setCalling] = useState<channelWa.PhoneNumber | null>(null);
  const { available: voiceAvailable } = useVoiceStatus();
  /*
   * The guided move out of the WhatsApp Business app. Reached from the
   * secondary action in the header, from the empty state, and from any
   * refusal that names a number on the app (the attach form hands the number
   * up here so the modal opens above the drawer rather than inside it).
   */
  const [moving, setMoving] = useState<{ msisdn?: string; phone_number_id?: string } | null>(null);
  // §4 item 5: the Embedded Signup notice was permanent and undismissable.
  const [noticeDismissed, setNoticeDismissed] = usePersistedState('tenant.numbers.esNotice.dismissed', false);

  const { data, isPending, error } = useQuery({
    queryKey: ['phone-numbers'],
    queryFn: () => channelWa.listPhoneNumbers()
  });

  /*
   * Who Meta would bill for a number connected with Facebook (5 October 2026).
   * With our credit line it is us, and Connect WhatsApp is simply the way in.
   * Without it, Continue with Facebook connects the business's own WhatsApp
   * account, which Meta bills directly, so the page sets out the two choices
   * side by side instead of three buttons that look alike.
   */
  const preflightQ = useQuery({
    queryKey: ['embedded-signup', 'preflight'],
    queryFn: () => channelWa.embeddedSignupPreflight(),
    staleTime: 60_000,
    retry: false
  });
  const ownMode = preflightQ.data?.data.payer === 'customer';
  const [ownChecklistDismissed, setOwnChecklistDismissed] = usePersistedState('tenant.numbers.ownChecklist.dismissed', false);

  const rows = (data?.data ?? []) as Indexed<channelWa.PhoneNumber>[];

  // Numbers Meta is refusing everything on. Absent on every healthy number, so
  // this is empty for almost every tenant on almost every visit.
  const blocked = rows.filter((n) => !!n.send_block);

  /*
   * What the plan allows, and what is in use. Without this the only way to
   * learn a plan is full is to walk through Meta's whole window and be refused
   * at the very last step, which is the worst place to find out.
   */
  const allowance = data?.meta?.allowance;
  // WhatsApp's limit on new people a day, per number (2 October 2026).
  const dailyLimits = data?.meta?.daily_limits ?? [];
  const atLimit = Boolean(
    allowance && !allowance.unlimited &&
    allowance.allowance != null && allowance.current >= allowance.allowance
  );

  // Numbers on the business's own WhatsApp account, and whether the plan still includes it.
  const ownNumbers = rows.filter((n) => n.meta_billing_payer === 'customer');
  const ownAccountAllowed = data?.meta?.own_account?.allowed ?? true;
  const ownPlanName = data?.meta?.own_account?.plan_name ?? null;
  const refresh = () => qc.invalidateQueries({ queryKey: ['phone-numbers'] });

  const update = useMutation({
    mutationFn: ({ id, req }: { id: string; req: channelWa.UpdatePhoneNumberRequest }) =>
      channelWa.updatePhoneNumber(id, req),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['phone-numbers'] });
      notifySuccess(
        res.data.is_active
          ? t('numbers.toast.activated', { number: label(res.data) })
          : t('numbers.toast.deactivated', { number: label(res.data) }),
        t('nav.numbers')
      );
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('toast.saveFailed'))
  });

  const remove = useMutation({
    mutationFn: (id: string) => channelWa.removePhoneNumber(id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['phone-numbers'] });
      notifySuccess(t('numbers.remove.toast', { number: label(res.data) }), t('nav.numbers'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('numbers.remove.failedTitle'))
  });

  /*
   * Removing a number from the account.
   *
   * The order is the customer's own instinct and the service enforces it: take
   * it out of service first, then remove it. That makes an accidental removal
   * two deliberate steps, and by the second one they have already watched what
   * stops working.
   *
   * The confirmation says what is kept as plainly as what stops, because both
   * are things people reasonably fear. Past conversations and billing history
   * stay; the number stops sending and receiving and leaves this account; and
   * nothing changes on their WhatsApp account with Meta.
   */
  function confirmRemove(n: channelWa.PhoneNumber) {
    openConfirmModal({
      title: t('numbers.remove.title', { number: label(n) }),
      children: (
        <Stack gap={8}>
          <Text role="bodySmall">{t('numbers.remove.stops')}</Text>
          <Text role="bodySmall">{t('numbers.remove.kept')}</Text>
          <Text role="caption" c="var(--rr-color-text-muted)">{t('numbers.remove.meta')}</Text>
          <Text role="caption" c="var(--rr-color-text-muted)">{t('numbers.remove.readd')}</Text>
        </Stack>
      ),
      labels: { confirm: t('numbers.remove.confirm'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate(n.id)
    });
  }

  function confirmDeactivate(n: channelWa.PhoneNumber) {
    openConfirmModal({
      title: t('numbers.deactivate.title', { number: label(n) }),
      children: (
        <Stack gap={8}>
          <Text role="bodySmall">{t('numbers.deactivate.body')}</Text>
          {/* Taking a number out of service stops every send on it, including
              broadcasts already scheduled against it. */}
          <Text role="caption" c="var(--rr-color-text-muted)">{t('numbers.deactivate.consequence')}</Text>
        </Stack>
      ),
      labels: { confirm: t('numbers.deactivate.confirm'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => update.mutate({ id: n.id, req: { is_active: false } })
    });
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('numbers.title')}
        description={t('numbers.description')}
        withDivider={false}
        // W22: connecting through Meta's own window is the way in now, and
        // the manual form stays alongside it. Someone whose Meta app is not
        // set up for the guided flow, or who is moving a number an operator
        // already registered, still needs the form.
        actions={ownMode ? <HelpButton guide="account" /> : (
          <Group gap="xs" wrap="wrap" data-guide="numbers-connect">
            <HelpButton guide="account" />
            <ConnectWhatsApp notices={false} onUnavailable={setConnectNotice} onConnected={() => qc.invalidateQueries({ queryKey: ['phone-numbers'] })} />
            <Button size="sm" variant="default" onClick={() => setAttaching(true)}>{t('numbers.actions.attach')}</Button>
            <Button size="sm" variant="subtle" onClick={() => setMoving({})}>{t('numbers.move.action')}</Button>
          </Group>
        )}
      />

      {/* Two ways in, said plainly: on our account (paid in credits) or the business's own (billed by Meta). */}
      {ownMode && (
        <SimpleGrid data-guide="numbers-connect" cols={{ base: 1, md: 2 }} spacing="md" data-testid="number-choices">
          <Card withBorder>
            <Stack gap="sm">
              <Text role="subtitle" fw={700}>{t('numbers.choice.shared.title')}</Text>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('numbers.choice.shared.body')}</Text>
              <Group gap="xs" wrap="wrap">
                <Button size="sm" variant="default" onClick={() => setAttaching(true)}>{t('numbers.actions.attach')}</Button>
                <Button size="sm" variant="subtle" onClick={() => setMoving({})}>{t('numbers.move.action')}</Button>
              </Group>
            </Stack>
          </Card>
          <Card withBorder>
            <Stack gap="sm">
              <Text role="subtitle" fw={700}>{t('numbers.choice.own.title')}</Text>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('numbers.choice.own.body')}</Text>
              <Group gap="xs" wrap="wrap">
                <ConnectWhatsApp onConnected={refresh} />
              </Group>
            </Stack>
          </Card>
        </SimpleGrid>
      )}

      {/*
        The business's own WhatsApp account after a move to a plan without it:
        those numbers stop sending until the plan includes it again. Messages to
        them still arrive.
      */}
      {ownNumbers.length > 0 && !ownAccountAllowed && (
        <Alert color="error" role="alert" title={t('numbers.own.offPlan.title')} data-testid="own-account-off-plan">
          <Stack gap={6}>
            <Text role="bodySmall">
              {ownPlanName
                ? t('numbers.own.offPlan.bodyNamed', { plan: ownPlanName, numbers: ownNumbers.map(label).join(', ') })
                : t('numbers.own.offPlan.body', { numbers: ownNumbers.map(label).join(', ') })}
            </Text>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('numbers.own.offPlan.incoming')}</Text>
            <Link to={OWN_ACCOUNT_UPGRADE_PATH}>{t('numbers.own.upgrade')}</Link>
          </Stack>
        </Alert>
      )}

      {/* What is left to do on the business's own account: a payment method in Meta. */}
      {ownNumbers.length > 0 && ownAccountAllowed && !ownChecklistDismissed && (
        <PaymentMethodChecklist count={ownNumbers.length} onClose={() => setOwnChecklistDismissed(true)} />
      )}

      {/* Why the guided connection cannot run right now, said once for the
          whole page rather than inside each button row. */}
      {connectNotice && <Alert color="info">{connectNotice}</Alert>}

      {/* "1 of 1 used", said before anyone starts. A plan with no ceiling says
          nothing at all: a count against infinity is not information. */}
      {allowance && !allowance.unlimited && allowance.allowance != null && (
        <Text role="bodySmall" c={atLimit ? 'var(--rr-color-warning)' : 'var(--rr-color-text-muted)'}>
          {atLimit && allowance.plan_name
            ? t('numbers.allowance.full', { plan: allowance.plan_name, allowance: allowance.allowance })
            : t('numbers.allowance.used', { current: allowance.current, allowance: allowance.allowance })}
        </Text>
      )}

      {error && <Alert color="error" role="alert">{t('numbers.loadError')}</Alert>}

      {/*
        A number that cannot send anything at all.

        Meta accepts every message on such a number over the API and refuses
        all of them afterwards on the status webhook, so before this the page
        showed "Active" beside a growing pile of failed messages and never
        joined the two. The commonest cause by far is 131042: no payment
        method on the customer's own WhatsApp Business Account, which nobody
        on this side can add for them. So it is said here, in front of the
        table, with the place to fix it as a link rather than a sentence.
      */}
      {/* A number Meta disconnected because it is also on the WhatsApp Business app (coexistence). */}
      {rows.filter((n) => n.is_coexistence && n.disconnected_at).map((n) => (
        <Alert key={`disconnected-${n.id}`} color="error" role="alert" title={t('numbers.coexist.disconnected.title', { number: n.msisdn ?? n.display_name ?? '' })} data-testid="coexistence-disconnected">
          <Stack gap={4}>
            <Text role="bodySmall">{t(`numbers.coexist.disconnected.${n.disconnect_reason ?? 'other'}`, t('numbers.coexist.disconnected.other'))}</Text>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('numbers.coexist.disconnected.fix')}</Text>
          </Stack>
        </Alert>
      ))}

      {blocked.map((n) => (
        <Alert key={n.id} color="error" role="alert" title={t('numbers.sendBlock.title')}>
          <Stack gap={6}>
            <Text role="bodySmall">
              {/* On the business's own account a billing refusal is theirs to fix in Meta; on ours it is ours. */}
              {n.send_block!.code === 'ACCOUNT_NOT_PAYABLE' && n.meta_billing_payer === 'customer'
                ? t('numbers.sendBlock.ACCOUNT_NOT_PAYABLE.customer')
                : t(`numbers.sendBlock.${n.send_block!.code}`, t('numbers.sendBlock.META_REFUSED'))}
            </Text>
            <Text role="caption" c="var(--rr-color-text-muted)">
              {/* Said once, plainly, because "am I paying for these?" is the
                  first thing anybody asks on seeing a wall of failures. */}
              {t('numbers.sendBlock.notCharged')}
              {n.send_block!.since ? ` ${t('numbers.sendBlock.since', { when: formatDateTime(n.send_block!.since) })}` : ''}
            </Text>
            {/* Only a number still on its owner's own Meta account can fix this
                at Meta. For every other number Meta bills us (21 September
                2026), and sending the customer there would be wrong. */}
            {n.send_block!.code === 'ACCOUNT_NOT_PAYABLE' && n.meta_billing_payer === 'customer' && (
              // A link, not a button that opens one: the place to fix this is
              // outside this product entirely, and the reader should be able
              // to see where they are being sent before they go.
              <a href={META_PAYMENTS_URL} target="_blank" rel="noreferrer noopener">
                {t('numbers.sendBlock.fix')}
              </a>
            )}
          </Stack>
        </Alert>
      ))}

      {/* Shown only while there is something to explain it against; an
          established tenant does not need to be told this on every visit. */}
      {rows.length > 0 && !noticeDismissed && !ownMode && (
        <Alert color="info" onClose={() => setNoticeDismissed(true)} withCloseButton>
          {t('numbers.connect.notice')}
        </Alert>
      )}

      {!isPending && rows.length === 0 ? (
        <Card withBorder data-guide="numbers-list">
          {/*
            §4 item 3 and §5: the empty state was a line of grey text on the one
            screen where a new tenant is definitionally stuck. The failure mode
            here is not a confusing UI, it is a user who does not know what a
            "Meta phone-number-id" is and has nowhere to find out, so the page
            carries the instructions rather than pointing elsewhere.
          */}
          <Stack gap="lg">
            {/*
              §4 item 3 and §5: the empty state is the one screen where a new
              account is definitionally stuck, so the guided connection is the
              action here and the by-hand route sits behind it rather than
              being the only way through.
            */}
            <EmptyState
              title={t('numbers.empty')}
              description={t(ownMode ? 'numbers.choice.emptyHint' : 'numbers.connect.description')}
              action={ownMode ? undefined : (
                <Group gap="xs" wrap="wrap">
                  <ConnectWhatsApp size="md" notices={false} onUnavailable={setConnectNotice} onConnected={() => qc.invalidateQueries({ queryKey: ['phone-numbers'] })} />
                  <Button variant="default" onClick={() => setAttaching(true)}>{t('numbers.actions.attach')}</Button>
                  <Button variant="subtle" onClick={() => setMoving({})}>{t('numbers.move.action')}</Button>
                </Group>
              )}
            />
            <Text role="caption" c="var(--rr-color-text-muted)">{t('numbers.setup.manualHeading')}</Text>
            <div className="rr-numbers__steps">
            <Stepper active={-1} orientation="vertical">
              <Stepper.Step label={t('numbers.setup.step1')} description={t('numbers.setup.step1.hint')} />
              <Stepper.Step
                label={t('numbers.setup.step2')}
                description={
                  <>
                    {t('numbers.setup.step2.hint')}{' '}
                    <a href={META_NUMBERS_URL} target="_blank" rel="noreferrer noopener">
                      {t('numbers.setup.openMeta')}
                    </a>
                  </>
                }
              />
              <Stepper.Step label={t('numbers.setup.step3')} description={t('numbers.setup.step3.hint')} />
            </Stepper>
            </div>
          </Stack>
        </Card>
      ) : (
        <Card withBorder noPadding data-guide="numbers-list">
          <DataTable<Indexed<channelWa.PhoneNumber>>
            records={rows}
            loading={isPending}
            idAccessor="id"
            emptyState={t('numbers.empty')}
            columns={[
              {
                accessor: 'msisdn',
                title: t('numbers.columns.msisdn'),
                // §4 item 9: msisdn is nullable and often blank, so the primary
                // identifier column frequently read "–" on a page listing
                // "phone numbers". The display name is the next-best identity.
                render: (n) => n.msisdn
                  ? <code>{n.msisdn}</code>
                  : <Text role="caption" c="var(--rr-color-text-muted)">{t('numbers.noMsisdn')}</Text>
              },
              {
                accessor: 'display_name',
                title: t('numbers.columns.displayName'),
                render: (n) => <NameCell number={n} onChanged={refresh} />
              },
              {
                accessor: 'meta_phone_number_id',
                title: t('numbers.columns.metaPhoneNumberId'),
                // §4 item 13: 11px monospace, below the app's readable minimum,
                // for the value users most often need to copy back to Meta.
                render: (n) => (
                  <Group gap={4} wrap="nowrap">
                    <code className="rr-numbers__meta-id">{n.meta_phone_number_id}</code>
                    <CopyButton value={n.meta_phone_number_id}>
                      {({ copied, failed, copy }) => (
                        <Button size="xs" variant="subtle" onClick={copy}>
                          {failed ? t('common.copyFailed') : copied ? t('common.copied') : t('action.copy')}
                        </Button>
                      )}
                    </CopyButton>
                  </Group>
                )
              },
              {
                accessor: 'quality_rating',
                title: t('numbers.columns.quality'),
                // §4 item 2: this was permanently "–" because nothing wrote
                // `quality_rating`. channel-wa now records Meta's quality
                // webhook, so "not rated yet" is a real state rather than a
                // column that could never fill.
                render: (n) => <QualityCell number={n} daily={dailyLimits.find((d) => d.number_id === n.id)} />
              },
              {
                accessor: 'is_active',
                title: t('common.status'),
                // A number Meta is refusing everything on is not "Active",
                // whatever this platform's own is_active flag says: the flag
                // means "we will try", not "it works".
                render: (n) => (
                  <Group gap={4} wrap="wrap">
                    {n.send_block ? (
                      <StatusBadge status="down">{t('numbers.sendBlock.badge')}</StatusBadge>
                    ) : (
                      <StatusBadge status={n.is_active ? 'healthy' : 'inactive'}>
                        {n.is_active ? t('status.active') : t('status.inactive')}
                      </StatusBadge>
                    )}
                    {n.calling_status === 'on' && <Badge variant="light" data-testid="calls-on">{t('numbers.calling.badge')}</Badge>}
                    {/* The business's own WhatsApp account: Meta bills the business, not credits. */}
                    {n.meta_billing_payer === 'customer' && (
                      <Tooltip label={t('numbers.own.badge.tooltip')} position="top">
                        <Badge variant="light" data-testid="own-account-badge">{t('numbers.own.badge')}</Badge>
                      </Tooltip>
                    )}
                    {/* Coexistence (3 October 2026): the number is also on the WhatsApp Business app. */}
                    {n.is_coexistence && !n.disconnected_at && (
                      <Tooltip label={t(`numbers.coexist.history.${n.history_sync_status ?? 'none'}`, { progress: n.history_progress ?? 0 })} position="top">
                        <Badge variant="light" data-testid="coexistence-badge">{t('numbers.coexist.badge')}</Badge>
                      </Tooltip>
                    )}
                  </Group>
                )
              },
              {
                accessor: 'created_at',
                title: t('common.added'),
                render: (n) => formatDateTime(n.created_at)
              },
              {
                accessor: 'actions',
                title: t('common.actions'),
                render: (n) => (
                  <Menu>
                    <Menu.Target>
                      <Button size="xs" variant="subtle" aria-label={t('common.actions')}>⋯</Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item onClick={() => setEditing(n)}>{t('action.edit')}</Menu.Item>
                      {voiceAvailable && (
                        <Menu.Item onClick={() => setCalling(n)}>{t('numbers.calling.action')}</Menu.Item>
                      )}
                      {/* The profile a recipient sees when they tap this
                          number. Every number on this platform showed a bare
                          number until it was filled in, because nothing
                          anywhere wrote it. */}
                      <Menu.Item onClick={() => navigate(`/numbers/${n.id}/profile`)}>
                        {t('numbers.profile.action')}
                      </Menu.Item>
                      {n.is_active ? (
                        <Menu.Item color="red" onClick={() => confirmDeactivate(n)}>
                          {t('numbers.deactivate.action')}
                        </Menu.Item>
                      ) : (
                        <Menu.Item onClick={() => update.mutate({ id: n.id, req: { is_active: true } })}>
                          {t('numbers.reactivate.action')}
                        </Menu.Item>
                      )}
                      {/* Only offered once the number is out of service, which
                          is both what the service enforces and what people
                          expect. Offering it greyed out on an active number
                          would be a puzzle; saying why is an answer. */}
                      {n.is_active ? (
                        <Tooltip label={t('numbers.remove.blocked')} position="left">
                          <div>
                            <Menu.Item disabled>{t('numbers.remove.action')}</Menu.Item>
                          </div>
                        </Tooltip>
                      ) : (
                        <Menu.Item color="red" onClick={() => confirmRemove(n)}>
                          {t('numbers.remove.action')}
                        </Menu.Item>
                      )}
                    </Menu.Dropdown>
                  </Menu>
                )
              }
            ]}
          />
          <div className="rr-numbers__count">
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('numbers.count', { count: rows.length })}
            </Text>
          </div>
        </Card>
      )}

      <AttachDrawer
        opened={attaching}
        onClose={() => setAttaching(false)}
        onAttached={() => qc.invalidateQueries({ queryKey: ['phone-numbers'] })}
        onMove={(n) => { setAttaching(false); setMoving(n); }}
      />

      <MoveFromApp
        opened={moving !== null}
        onClose={() => setMoving(null)}
        initialMsisdn={moving?.msisdn}
        initialPhoneNumberId={moving?.phone_number_id}
        onConnected={() => qc.invalidateQueries({ queryKey: ['phone-numbers'] })}
      />

      <CallingDrawer number={calling} onClose={() => setCalling(null)} />

      <EditDrawer
        number={editing}
        onClose={() => setEditing(null)}
        onSaved={() => qc.invalidateQueries({ queryKey: ['phone-numbers'] })}
      />
    </Stack>
  );
}

/** A number's best available human identity: msisdn, else name, else Meta id. */
function label(n: { msisdn?: string | null; display_name?: string | null; id?: string }): string {
  return n.msisdn || n.display_name || n.id || '';
}

function QualityCell({ number: n, daily }: { number: channelWa.PhoneNumber; daily?: channelWa.NumberDailyLimit }) {
  const t = useT();
  // How many new people this number has messaged today against WhatsApp's
  // limit: what a broadcast can still reach. Templates only; replies in a
  // conversation the customer opened do not count.
  const usage = daily && daily.limit != null ? (
    <Tooltip label={dailyTooltip(t, daily)} position="top" maxWidth={340}>
      <Text role="caption" c={daily.remaining === 0 ? 'var(--rr-color-danger)' : 'var(--rr-color-text-muted)'}>
        {dailyLine(t, daily)}
      </Text>
    </Tooltip>
  ) : null;

  if (!n.quality_rating) {
    return (
      <Group gap={6} wrap="nowrap">
        <Tooltip label={t('numbers.quality.unrated.tooltip')} position="top">
          <Text role="caption" c="var(--rr-color-text-muted)">{t('numbers.quality.unrated')}</Text>
        </Tooltip>
        {usage}
      </Group>
    );
  }

  const tone = n.quality_rating === 'GREEN' ? 'healthy'
    : n.quality_rating === 'RED' ? 'down'
    : 'warning';

  return (
    <Group gap={6} wrap="nowrap">
      <Tooltip
        label={n.quality_updated_at
          ? t('numbers.quality.updated', { when: formatDateTime(n.quality_updated_at) })
          : t('numbers.quality.unrated.tooltip')}
        position="top"
      >
        <StatusBadge status={tone}>{t(`numbers.quality.${n.quality_rating.toLowerCase()}`, n.quality_rating)}</StatusBadge>
      </Tooltip>
      {/* The messaging tier is the figure that answers "can this number carry
          my 5 000-recipient broadcast today?". */}
      {n.messaging_limit_tier && (
        <Badge size="xs" variant="light">{t('numbers.tier', { tier: tierLabel(n.messaging_limit_tier) })}</Badge>
      )}
      {usage}
    </Group>
  );
}

/*
 * The daily line. On our shared WhatsApp account the limit is the account's,
 * shared fairly between the businesses on it, so the figure is this
 * business's share of today's limit (5 October 2026). After 18:00 spare room
 * is released, and the figure becomes what this business may still reach.
 * A number on the business's own account keeps its own limit.
 */
function dailyLine(t: Translate, daily: channelWa.NumberDailyLimit): string {
  const limit = formatCount(daily.limit ?? 0);
  if (!daily.shared) {
    return daily.remaining === 0
      ? t('numbers.daily.full', { limit })
      : t('numbers.daily.used', { used: formatCount(daily.used), limit });
  }
  if (daily.released) {
    return daily.remaining === 0
      ? t('numbers.daily.shared.fullReleased')
      : t('numbers.daily.shared.released', { used: formatCount(daily.used), remaining: formatCount(daily.remaining ?? 0) });
  }
  return daily.remaining === 0
    ? t('numbers.daily.shared.full', { limit })
    : t('numbers.daily.shared.used', { used: formatCount(daily.used), limit });
}

function dailyTooltip(t: Translate, daily: channelWa.NumberDailyLimit): string {
  if (!daily.shared) return t('numbers.daily.tooltip');
  const share = t('numbers.daily.shared.tooltip', {
    share: formatCount(daily.share ?? daily.limit ?? 0),
    limit: formatCount(daily.account_limit ?? 0),
    count: daily.businesses ?? 1
  });
  return daily.released ? `${share} ${t('numbers.daily.shared.tooltipReleased')}` : share;
}

/*
 * Meta's review of a number's display name, in plain words. Meta's own state
 * names are kept as keys, and an unknown one (Meta adds them without notice)
 * reads as words rather than as a constant.
 */
function readableCode(code: string): string {
  const words = code.replace(/_/g, ' ').toLowerCase().trim();
  return words ? words[0].toUpperCase() + words.slice(1) : code;
}

function nameStatusText(t: Translate, status: string | null | undefined, reason: string | null | undefined): string {
  if (!status) return t('numbers.name.status.unchecked');
  const s = status.toUpperCase();
  if (s === 'DECLINED' && reason) {
    return t('numbers.name.status.DECLINED.reason', {
      reason: t(`numbers.name.reason.${reason.toUpperCase()}`, readableCode(reason))
    });
  }
  return t(`numbers.name.status.${s}`, readableCode(s));
}

function nameStatusColour(status: string | null | undefined): string {
  switch ((status ?? '').toUpperCase()) {
    case 'APPROVED':
    case 'AVAILABLE_WITHOUT_REVIEW': return 'var(--rr-color-success)';
    case 'PENDING_REVIEW': return 'var(--rr-color-warning)';
    case 'DECLINED':
    case 'EXPIRED': return 'var(--rr-color-error)';
    default: return 'var(--rr-color-text-muted)';
  }
}

/**
 * The display name, Meta's review of it, and how to change it (5 October 2026).
 *
 * A number on our shared account changes its name through us: the business
 * asks here, we make the change in WhatsApp Manager, and Meta reviews it. A
 * number on the business's own account changes its name in its own WhatsApp
 * Manager, which is linked rather than described.
 */
function NameCell({ number: n, onChanged }: { number: channelWa.PhoneNumber; onChanged: () => void }) {
  const t = useT();
  const [asking, setAsking] = useState(false);
  const own = n.meta_billing_payer === 'customer';
  const check = useMutation({
    mutationFn: () => channelWa.refreshNumberName(n.id),
    onSuccess: (res) => {
      onChanged();
      if (res.data.checked_now) {
        notifySuccess(t('numbers.name.label', { status: nameStatusText(t, res.data.name_status, res.data.name_rejection_reason) }), label(n));
      } else {
        notifyError(t('numbers.name.checkUnavailable'), label(n));
      }
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('numbers.name.checkFailed'))
  });

  return (
    <Stack gap={2}>
      {n.display_name
        ? <Text role="bodySmall">{n.display_name}</Text>
        : <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text>}
      <Group gap={4} wrap="wrap" align="center">
        <Tooltip
          label={n.name_status_at ? t('numbers.name.checkedAt', { when: formatDateTime(n.name_status_at) }) : t('numbers.name.status.unchecked.tooltip')}
          position="top"
        >
          <Text role="caption" c={nameStatusColour(n.name_status)} data-testid="name-status">
            {t('numbers.name.label', { status: nameStatusText(t, n.name_status, n.name_rejection_reason) })}
          </Text>
        </Tooltip>
        <Button size="xs" variant="subtle" loading={check.isPending} onClick={() => check.mutate()}>{t('numbers.name.check')}</Button>
      </Group>
      {!own && n.name_requested && (
        <Text role="caption" c="var(--rr-color-text-muted)" data-testid="name-requested">
          {n.name_requested_at
            ? t('numbers.name.requested', { name: n.name_requested, when: formatDate(n.name_requested_at) })
            : t('numbers.name.requestedUndated', { name: n.name_requested })}
        </Text>
      )}
      {own ? (
        <Text role="caption">
          <a href={META_NUMBERS_URL} target="_blank" rel="noreferrer noopener">{t('numbers.name.changeInManager')}</a>
        </Text>
      ) : (
        <Group gap={4}>
          <Button size="xs" variant="subtle" onClick={() => setAsking(true)}>{t('numbers.name.ask')}</Button>
        </Group>
      )}
      {!own && (
        <AskNameModal number={n} opened={asking} onClose={() => setAsking(false)} onSent={onChanged} />
      )}
    </Stack>
  );
}

function AskNameModal({ number: n, opened, onClose, onSent }: {
  number: channelWa.PhoneNumber;
  opened: boolean;
  onClose: () => void;
  onSent: () => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const ask = useMutation({
    mutationFn: (value: string) => channelWa.requestNumberName(n.id, value),
    onSuccess: (res) => {
      notifySuccess(t('numbers.name.ask.sent', { name: res.data.name_requested ?? name.trim() }), label(n));
      onSent();
      setName('');
      onClose();
    },
    onError: (e) => {
      if (isApiError(e) && e.code === channelWa.NAME_INVALID) setNameError(t('numbers.name.ask.length'));
      else if (isApiError(e) && e.code === channelWa.OWN_ACCOUNT_NAME) setNameError(t('numbers.name.ask.ownAccount'));
      else notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('numbers.name.ask.failed'));
    }
  });

  function submit() {
    const value = name.trim();
    if (value.length < 3 || value.length > 100) { setNameError(t('numbers.name.ask.length')); return; }
    ask.mutate(value);
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t('numbers.name.ask.title', { number: label(n) })} size="md">
      <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <Stack gap="md">
          <Text role="bodySmall">{t('numbers.name.ask.body')}</Text>
          {n.display_name && (
            <Text role="caption" c="var(--rr-color-text-muted)">{t('numbers.name.ask.current', { name: n.display_name })}</Text>
          )}
          <TextInput
            label={t('numbers.name.ask.field')}
            description={t('numbers.name.ask.hint')}
            value={name}
            error={nameError}
            maxLength={100}
            required
            onChange={(e) => { setName(e.currentTarget.value); setNameError(''); }}
          />
          <Group justify="flex-end" gap="sm">
            <Button type="button" variant="default" onClick={onClose}>{t('action.cancel')}</Button>
            <Button type="submit" loading={ask.isPending}>{t('numbers.name.ask.submit')}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

/** "TIER_1K" reads as a database constant; "1 000/day" reads as a limit. */
function tierLabel(tier: string): string {
  const map: Record<string, string> = {
    TIER_50: '50',
    TIER_250: '250',
    TIER_1K: '1 000',
    TIER_10K: '10 000',
    TIER_100K: '100 000',
    TIER_UNLIMITED: '∞'
  };
  return map[tier.toUpperCase()] ?? tier;
}

function AttachDrawer({
  opened,
  onClose,
  onAttached,
  onMove
}: {
  opened: boolean;
  onClose: () => void;
  onAttached: () => void;
  /** A refusal named a number on the WhatsApp Business app; the page opens the guided move. */
  onMove: (number: { msisdn?: string; phone_number_id?: string }) => void;
}) {
  const t = useT();
  const [metaId, setMetaId] = useState('');
  // The canonical form the field hands back ('' while the number does not read).
  const [msisdn, setMsisdn] = useState('');
  const [msisdnBad, setMsisdnBad] = useState(false);
  const [msisdnError, setMsisdnError] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [metaIdError, setMetaIdError] = useState('');
  const [busy, setBusy] = useState(false);
  // WhatsApp's answer about the number, when it said no: what is wrong, what
  // to do, and the way into the guided move when the number is on the app.
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  async function attach() {
    const id = metaId.trim();
    // §4 item 7 and §5: validation was a toast, and shape was never checked at
    // all, so a typo'd id was stored and only surfaced as a Meta failure at
    // send time, where the inbox rendered it as "HTTP 404".
    if (!id) { setMetaIdError(t('numbers.attach.toast.metaIdRequired')); return; }
    if (!META_ID_SHAPE.test(id)) { setMetaIdError(t('numbers.attach.form.metaIdShape')); return; }
    // The field already says why; the submit just refuses to carry it.
    if (msisdnBad) { setMsisdnError(t('phone.fixNumber')); return; }

    setBusy(true);
    setRefusal(null);
    try {
      const res = await channelWa.attachPhoneNumber({
        meta_phone_number_id: id,
        msisdn: msisdn || undefined,
        display_name: displayName || undefined,
        waba_id: wabaId || undefined
      });
      // Name the number, rather than "Attached." with no subject.
      const shown = msisdn || displayName || id;
      notifySuccess(
        res.data.attached
          ? t('numbers.attach.toast.attachedNamed', { number: shown })
          : t('numbers.attach.toast.updatedNamed', { number: shown }),
        t('nav.numbers')
      );
      onAttached();
      onClose();
      setMetaId(''); setMsisdn(''); setMsisdnBad(false); setMsisdnError(''); setDisplayName(''); setWabaId(''); setMetaIdError('');
    } catch (e) {
      const refused = phoneRefusalText(e, t);
      // The plan refusal carries the sentence to show, so a customer who has
      // run out of numbers reads what their plan includes and what to do about
      // it rather than a status code.
      if (refused) {
        setMsisdnError(refused);
      } else if (channelWa.isPlanLimitReached(e)) {
        setMetaIdError(e.body.error.message);
        notifyError(e.body.error.message, t('numbers.attach.toast.failedTitle'));
      } else if (refusalFrom(e)) {
        // Rendered in the form, not in a toast: the refusal carries a next
        // step and, for a number on the app, the button that leads out.
        setRefusal(refusalFrom(e));
      } else {
        notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('numbers.attach.toast.failedTitle'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer opened={opened} onClose={onClose} title={t('numbers.attach.title')} position="right" size="md">
      {/* A form, so Enter submits. */}
      <form onSubmit={(e) => { e.preventDefault(); void attach(); }}>
        <Stack gap="md" style={{ padding: 4 }}>
          {refusal && (
            <MetaRefusalNotice
              refusal={refusal}
              onClose={() => setRefusal(null)}
              onMove={(n) => onMove({ msisdn: n.msisdn ?? msisdn ?? undefined, phone_number_id: n.phone_number_id ?? metaId.trim() })}
            />
          )}
          <TextInput
            label={t('numbers.columns.metaPhoneNumberId')}
            description={t('numbers.attach.form.metaPhoneNumberIdHint')}
            error={metaIdError}
            value={metaId}
            onChange={(e) => { setMetaId(e.currentTarget.value); setMetaIdError(''); }}
            required
            inputMode="numeric"
          />
          <Text role="caption" c="var(--rr-color-text-muted)">
            <a href={META_NUMBERS_URL} target="_blank" rel="noreferrer noopener">
              {t('numbers.setup.openMeta')}
            </a>
          </Text>
          <PhoneInput
            label={t('numbers.attach.form.msisdn')}
            description={`${t('phone.hint')} ${t('numbers.attach.form.msisdnHint')}`}
            value={msisdn}
            error={msisdnError}
            onChange={(e164, meta) => { setMsisdn(e164); setMsisdnBad(!meta.valid && meta.raw.trim() !== ''); setMsisdnError(''); }}
            labels={phoneLabels(t)}
          />
          <TextInput label={t('numbers.columns.displayName')} value={displayName} onChange={(e) => setDisplayName(e.currentTarget.value)} />
          <TextInput label={t('numbers.attach.form.wabaId')} description={t('numbers.attach.form.wabaIdHint')} value={wabaId} onChange={(e) => setWabaId(e.currentTarget.value)} />
          <Group justify="flex-end" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
            <Button type="button" variant="default" onClick={onClose}>{t('action.cancel')}</Button>
            <Button type="submit" loading={busy}>{t('numbers.attach.confirm')}</Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

/** §4 item 6: a typo'd display name could only be fixed by re-attaching. */
function EditDrawer({
  number,
  onClose,
  onSaved
}: {
  number: channelWa.PhoneNumber | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const [displayName, setDisplayName] = useState('');
  const [msisdn, setMsisdn] = useState('');
  const [msisdnBad, setMsisdnBad] = useState(false);
  const [msisdnError, setMsisdnError] = useState('');
  const [busy, setBusy] = useState(false);
  const [hydratedFor, setHydratedFor] = useState<string | null>(null);

  if (number && hydratedFor !== number.id) {
    setDisplayName(number.display_name ?? '');
    setMsisdn(number.msisdn ?? '');
    setHydratedFor(number.id);
  }

  async function save() {
    if (!number) return;
    if (msisdnBad) { setMsisdnError(t('phone.fixNumber')); return; }
    setBusy(true);
    try {
      await channelWa.updatePhoneNumber(number.id, {
        display_name: displayName,
        msisdn: msisdn || undefined
      });
      notifySuccess(t('numbers.toast.renamed', { number: msisdn || displayName }), t('nav.numbers'));
      onSaved();
      onClose();
      setHydratedFor(null);
    } catch (e) {
      const refused = phoneRefusalText(e, t);
      if (refused) setMsisdnError(refused);
      else notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('toast.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer opened={!!number} onClose={onClose} title={t('numbers.edit.title')} position="right" size="md">
      <form onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <Stack gap="md" style={{ padding: 4 }}>
          <TextInput label={t('numbers.columns.displayName')} value={displayName} onChange={(e) => setDisplayName(e.currentTarget.value)} />
          <PhoneInput
            label={t('numbers.attach.form.msisdn')}
            description={t('phone.hint')}
            value={msisdn}
            error={msisdnError}
            onChange={(e164, meta) => { setMsisdn(e164); setMsisdnBad(!meta.valid && meta.raw.trim() !== ''); setMsisdnError(''); }}
            labels={phoneLabels(t)}
          />
          <Group justify="flex-end" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
            <Button type="button" variant="default" onClick={onClose}>{t('action.cancel')}</Button>
            <Button type="submit" loading={busy}>{t('action.save')}</Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
